import type { ProductAnalyticsState } from '../../model/analytics'
import { Card } from '@/common/ui/Card'
import Markdown from 'react-markdown'
import './analyticsResult.css'

const getMarkerColor = (viewType?: string) => {
    switch (viewType) {
        case 'NEUTRAL':
            return '#be96ff'
        case 'SUCCESS':
            return '#be96ff'
        case 'WARNING':
        case 'DANGER':
            return '#ff9500'
        default:
            return '#aba7af'
    }
}

interface MarkdownContentProps {
    content?: string
    viewType?: string
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, viewType }) => {
    return (
        <div className='text-[#aba7af] text-sm leading-normal'>
            <Markdown
                components={{
                    ul: ({ children }) => <ul className='list-none pl-0 space-y-1'>{children}</ul>,
                    li: ({ children }) => (
                        <li className='relative pl-5'>
                            <span
                                className='absolute left-0 top-2 w-1.5 h-1.5 rounded-full'
                                style={{ backgroundColor: getMarkerColor(viewType) }}
                            />
                            {children}
                        </li>
                    ),
                }}
            >
                {content}
            </Markdown>
        </div>
    )
}

const isSpecialBlockType = (type?: string) => {
    return (
        type === 'IMPROVEMENTS' ||
        type === 'TITLE_SCORE' ||
        type === 'DESCRIPTION_CONTENT' ||
        type === 'ADDITION_VISUAL_ELEMENTS' ||
        type === 'MAIN_CONTENT'
    )
}

const getScoreColor = (score: number, maxScore: number) => {
    switch (true) {
        case score <= maxScore * 0.4:
            return 'linear-gradient(97deg, #E192AA -15.56%, #C83838 102.37%) text'
        case score <= maxScore * 0.7:
            return 'linear-gradient(97deg, #EAD3B3 -15.56%, #FE9500 102.37%) text'
        case score <= maxScore * 1:
            return 'linear-gradient(97deg, #A8FFA4 -15.56%, #198E13 102.37%) text'
    }
}

type AnalitycsResultProps = {
    analytics: ProductAnalyticsState
}

export const AnalitycsResult = ({ analytics }: AnalitycsResultProps) => {
    const { detailQuery, isSubmitting } = analytics
    const detail = detailQuery.data

    if (isSubmitting || !detail?.validation_result?.blocks) {
        return null
    }
    return (
        <>
            {detail.validation_result.blocks.map((block, index) => (
                <Card type={block.type === 'IMPROVEMENTS' ? 'result-last' : 'result'} className='w-full' key={index}>
                    <div className='flex flex-col gap-8 items-start'>
                        <h3 className='font-medium text-lg text-white'>{block.title}</h3>
                        <div
                            className={`flex items-start w-full ${isSpecialBlockType(block.type) && 'flex-col gap-5 items-start'}`}
                        >
                            {block.subblocks?.map((subblock, index) => (
                                <div className='flex-1 flex flex-col gap-4 items-start' key={index}>
                                    <h4 className='font-medium' style={{ color: getMarkerColor(subblock.view_type) }}>
                                        {subblock.title}
                                    </h4>
                                    <MarkdownContent content={subblock.content} viewType={subblock.view_type} />
                                </div>
                            ))}
                        </div>
                        {block.score !== null && (
                            <div
                                className='absolute top-[33px] right-8 text-[24px] font-semibold bg-clip-text text-transparent'
                                style={{
                                    background: getScoreColor(Number(block.score), Number(block.max_score) || 10),
                                    backgroundClip: 'text',
                                }}
                            >
                                {typeof block.score === 'number' ? block.score : 0}&#8202;/&#8202;
                                {typeof block.max_score === 'number'
                                    ? block.max_score
                                    : detail.validation_result?.max_score}
                            </div>
                        )}
                    </div>
                </Card>
            ))}
        </>
    )
}
